import { useEffect, useRef, useState } from 'react';

import type { Gutschein, KassenApi } from '../api';
import {
  erzeugeGutscheinCode,
  normalisiereCode,
  type GutscheinArt,
  type GutscheinAusgabe,
  type GutscheinEinloesung,
} from '../gutschein';
import { STEUERSAETZE, formatiereEuro, parseEuro, type Steuersatz } from '../warenkorb';
import { Bildschirmtastatur, TastaturKnopf, useBildschirmtastatur } from './Bildschirmtastatur';
import { useFokusfalleAmFenster } from './zahlung-v2/fokus';

/** Gutschein verkaufen: Art, Betrag, bei Einzweck Leistung und Steuersatz. */
export function GutscheinVerkaufDialog(props: {
  onFertig: (ausgabe: GutscheinAusgabe | null) => void;
}) {
  const [art, setArt] = useState<GutscheinArt>('mehrzweck');
  const [betrag, setBetrag] = useState('');
  const [steuersatz, setSteuersatz] = useState<Steuersatz>('10');
  const [bezeichnung, setBezeichnung] = useState('');
  const [gueltigBis, setGueltigBis] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  // BT (Codex-Prüfung BT v1, Auflage 3): Fokusfalle – Tab erreicht den Verkauf dahinter nicht
  const dialog = useRef<HTMLDivElement | null>(null);
  useFokusfalleAmFenster(dialog, true);

  function uebernehmen(): void {
    const cent = parseEuro(betrag);
    if (cent === null || cent <= 0) {
      setFehler('Betrag in Euro angeben, z. B. 50');
      return;
    }
    if (art === 'einzweck' && bezeichnung.trim() === '') {
      setFehler('Einzweck-Gutschein: Leistung bezeichnen (z. B. Frühstück für zwei)');
      return;
    }
    props.onFertig({
      code: erzeugeGutscheinCode(),
      art,
      nennwertCent: cent,
      ...(art === 'einzweck' ? { steuersatz, bezeichnung: bezeichnung.trim() } : {}),
      ...(gueltigBis === '' ? {} : { gueltigBis }),
    });
  }

  return (
    <div
      ref={dialog}
      className="zahlung-dialog"
      role="dialog"
      aria-modal="true"
      tabIndex={-1}
      style={{ outline: 'none' }}
    >
      <div className="karte" style={{ textAlign: 'left' }}>
        <h2>Gutschein verkaufen</h2>
        <div className="chips" role="group" aria-label="Gutscheinart">
          <button
            type="button"
            className={art === 'mehrzweck' ? 'aktiv' : ''}
            onClick={() => {
              setArt('mehrzweck');
            }}
          >
            Wertgutschein (Mehrzweck)
          </button>
          <button
            type="button"
            className={art === 'einzweck' ? 'aktiv' : ''}
            onClick={() => {
              setArt('einzweck');
            }}
          >
            Einzweck (feste Leistung)
          </button>
        </div>
        <p className="hinweis">
          {art === 'mehrzweck'
            ? 'Freie Warenwahl: Verkauf ohne Umsatzsteuer (Bonverkauf 0 %), die Steuer entsteht bei der Einlösung.'
            : 'Leistung und Steuersatz stehen fest: der Verkauf wird sofort mit diesem Satz versteuert, die Einlösung ist kein neuer Umsatz.'}
        </p>
        <div className="zeile">
          <label>
            Betrag €
            <input
              inputMode="decimal"
              autoFocus
              data-scansperre="Betragsfeld"
              value={betrag}
              onChange={(e) => {
                setBetrag(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  uebernehmen();
                }
              }}
            />
          </label>
          {art === 'einzweck' && (
            <>
              <label>
                Leistung
                <input
                  value={bezeichnung}
                  maxLength={60}
                  placeholder="z. B. Frühstück für zwei"
                  onChange={(e) => {
                    setBezeichnung(e.target.value);
                  }}
                />
              </label>
              <label>
                USt
                <select
                  value={steuersatz}
                  onChange={(e) => {
                    setSteuersatz(e.target.value as Steuersatz);
                  }}
                >
                  {STEUERSAETZE.map((s) => (
                    <option key={s.satz} value={s.satz}>
                      {s.text}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}
          <label>
            Gültig bis (optional)
            <input
              type="date"
              value={gueltigBis}
              onChange={(e) => {
                setGueltigBis(e.target.value);
              }}
            />
          </label>
        </div>
        {fehler !== null && <p className="fehler">{fehler}</p>}
        <div className="zeile">
          <button className="taste" onClick={uebernehmen}>
            In den Bon
          </button>
          <button
            className="taste zweit"
            onClick={() => {
              props.onFertig(null);
            }}
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
}

/** Gutschein einlösen: Code (Scanner oder Tastatur) prüfen, Betrag bis zum Rest übernehmen. */
export function GutscheinEinloesenDialog(props: {
  api: KassenApi;
  kassenId: string;
  /** Offener Rest des Bons (Vorschlag für den Einlösebetrag). */
  offenCent: number;
  /** Vom Scanner gelesener Code: wird sofort geprüft. */
  startCode?: string;
  onFertig: (einloesung: GutscheinEinloesung | null) => void;
  /** D7 (N10): bereits im Bon eingelöste Gutscheine – mit ✕ entfernbar (nur vor dem Abschluss). */
  einloesungen?: readonly GutscheinEinloesung[];
  onEntfernen?: (code: string) => void;
}) {
  const { api, kassenId } = props;
  const [code, setCode] = useState(props.startCode ?? '');
  const [stand, setStand] = useState<{ gutschein: Gutschein; einloesbar: boolean } | null>(null);
  const [betrag, setBetrag] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const eingabe = useRef<HTMLInputElement>(null);
  // BT (Codex-Prüfung BT v1, Auflage 3): Fokusfalle – Tab erreicht den Verkauf dahinter nicht
  const dialog = useRef<HTMLDivElement | null>(null);
  useFokusfalleAmFenster(dialog, true);
  /** BT2-D7: Tastatur für den Code, startet „Groß“ (BT-E3); „Prüfen ↵“ prüft nur (R2). */
  const bt = useBildschirmtastatur({ standardZiel: 'code' });

  useEffect(() => {
    eingabe.current?.focus();
    if (props.startCode !== undefined) {
      void pruefen(props.startCode);
    }
    // nur beim Öffnen
  }, []);

  async function pruefen(roh = code): Promise<void> {
    setFehler(null);
    setStand(null);
    const c = normalisiereCode(roh);
    setCode(c);
    setLaeuft(true);
    try {
      const r = await api.gutschein(kassenId, c);
      setStand(r);
      const vorschlag = Math.min(r.gutschein.restCent, Math.max(props.offenCent, 0));
      setBetrag(formatiereEuro(r.gutschein.art === 'einzweck' ? r.gutschein.restCent : vorschlag));
      if (!r.einloesbar) {
        setFehler(
          r.gutschein.gesperrt
            ? 'Gutschein ist gesperrt'
            : r.gutschein.restCent === 0
              ? 'Gutschein ist aufgebraucht'
              : `Gutschein ist seit ${r.gutschein.gueltigBis ?? '?'} abgelaufen`,
        );
      }
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  }

  function uebernehmen(): void {
    if (stand?.einloesbar !== true) {
      return;
    }
    const cent = parseEuro(betrag);
    if (cent === null || cent <= 0 || cent > stand.gutschein.restCent) {
      setFehler(`Betrag zwischen 0,01 und ${formatiereEuro(stand.gutschein.restCent)} € angeben`);
      return;
    }
    props.onFertig({
      code: stand.gutschein.code,
      art: stand.gutschein.art,
      betragCent: cent,
      steuersatz: stand.gutschein.steuersatz,
    });
  }

  const betragCent = parseEuro(betrag);
  /** D7: Rest nach dieser Einlösung (nur Wertgutschein; nur Anzeige). */
  const restNachher =
    stand?.einloesbar === true && stand.gutschein.art === 'mehrzweck' && betragCent !== null
      ? Math.max(props.offenCent - betragCent, 0)
      : null;
  const bereits = props.einloesungen ?? [];
  const codeFeld = bt.feld('code', {
    name: 'Gutscheincode',
    setzen: setCode,
    gross: true,
    bestaetigen: {
      text: 'Prüfen ↵',
      aktion: () => {
        if (!laeuft && code.trim() !== '') {
          void pruefen();
        }
      },
    },
  });

  // POS V2 Phase 2, Rahmen D7 `17:2823`: Code, Prüfergebnis, bereits eingelöste Gutscheine mit ✕,
  // Betrag „Jetzt einlösen“ – Prüfung, Betragsregeln und Buchung unverändert.
  return (
    <div
      ref={dialog}
      className="zahlung-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="gs-titel"
      tabIndex={-1}
      style={{ outline: 'none' }}
    >
      <div className={`karte dlg gutschein-dialog${bt.tastatur === null ? '' : ' mit-tastatur'}`}>
        <div className="dlg-kopf">
          <h2 id="gs-titel">Gutschein einlösen</h2>
          <span className="dlg-meta">Zu zahlen {formatiereEuro(props.offenCent)} €</span>
        </div>
        <div className="dlg-inhalt">
          <div className="gs-code">
            <label>
              <span className="dlg-feldname">Code (scannen oder tippen)</span>
              <input
                {...codeFeld}
                ref={(el) => {
                  eingabe.current = el;
                  codeFeld.ref(el);
                }}
                value={code}
                placeholder="GS-XXXX-XXXX-XXXX"
                autoCapitalize="characters"
                onChange={(e) => {
                  setCode(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    void pruefen();
                  }
                }}
              />
            </label>
            <TastaturKnopf
              steuerung={bt.knopf('code')}
              klein
              beschriftung="Tastatur für den Code"
            />
            <button
              className="taste zweit"
              disabled={laeuft || code.trim() === ''}
              onClick={() => void pruefen()}
            >
              Prüfen
            </button>
          </div>
          {stand !== null && (
            <div className={`dlg-info${stand.einloesbar ? ' dlg-erfolg' : ' dlg-warnung'}`}>
              <p>
                <strong>
                  {stand.gutschein.art === 'mehrzweck' ? 'Wertgutschein' : 'Einzweck-Gutschein'}
                  {stand.gutschein.bezeichnung !== null && ` „${stand.gutschein.bezeichnung}“`}
                  {stand.gutschein.steuersatz !== null &&
                    stand.gutschein.art === 'einzweck' &&
                    ` · ${stand.gutschein.steuersatz} %`}{' '}
                  · Rest {formatiereEuro(stand.gutschein.restCent)} €
                </strong>{' '}
                von {formatiereEuro(stand.gutschein.nennwertCent)} €
                {stand.gutschein.gueltigBis !== null &&
                  ` · gültig bis ${stand.gutschein.gueltigBis}`}
              </p>
              {stand.gutschein.art === 'mehrzweck' && stand.einloesbar && (
                <p className="hinweis">Zahlungsmittel zum Nominalwert (Mehrzweck).</p>
              )}
              {stand.gutschein.art === 'einzweck' && stand.einloesbar && (
                <p className="hinweis">
                  Die Leistung „{stand.gutschein.bezeichnung ?? 'Gutschein'}“ muss als Position im
                  Bon stehen; der Gutschein wird mit {stand.gutschein.steuersatz ?? '?'} % abgezogen
                  (bereits beim Verkauf versteuert).
                </p>
              )}
            </div>
          )}
          {bereits.length > 0 && props.onEntfernen !== undefined && (
            <div className="gs-bereits">
              <span className="dlg-feldname">Bereits eingelöst</span>
              <ul>
                {bereits.map((e) => (
                  <li key={e.code}>
                    <span>
                      {e.code} · {e.art === 'mehrzweck' ? 'Wertgutschein' : 'Einzweck-Gutschein'}
                    </span>
                    <strong>−{formatiereEuro(e.betragCent)} €</strong>
                    <button
                      type="button"
                      className="zeile-x"
                      aria-label={`Einlösung ${e.code} entfernen`}
                      onClick={() => {
                        props.onEntfernen?.(e.code);
                      }}
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {stand?.einloesbar === true && (
            <label className="gs-betrag">
              <span className="dlg-feldname">
                {stand.gutschein.art === 'einzweck'
                  ? 'Gegenwert €'
                  : `Einlösen € (höchstens offener Betrag ${formatiereEuro(Math.min(stand.gutschein.restCent, Math.max(props.offenCent, 0)))} €)`}
              </span>
              <input
                inputMode="decimal"
                data-scansperre="Betragsfeld"
                value={betrag}
                onChange={(e) => {
                  setBetrag(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    uebernehmen();
                  }
                }}
              />
            </label>
          )}
          {fehler !== null && <p className="fehler">{fehler}</p>}
        </div>
        {bt.tastatur !== null && <Bildschirmtastatur steuerung={bt.tastatur} />}
        <div className="dlg-fuss">
          <button
            className="taste zweit"
            data-kuerzel="Esc"
            onClick={() => {
              props.onFertig(null);
            }}
          >
            Schließen
          </button>
          {stand?.einloesbar === true && (
            <button className="taste" onClick={uebernehmen}>
              Einlösen
              {restNachher !== null && ` · Rest ${formatiereEuro(restNachher)} €`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
